import { ListUl } from "@styled-icons/boxicons-regular";
import {
    Edit,
    Envelope,
    Flag,
    Group,
    InfoCircle,
    Notepad,
    UserPlus,
    UserX,
} from "@styled-icons/boxicons-solid";
import isEqual from "lodash.isequal";
import { observer } from "mobx-react-lite";
import { Link, useHistory } from "react-router-dom";
import { API, UserPermission } from "revolt.js";
import styles from "./UserProfile.module.scss";
import { Localizer, Text } from "preact-i18n";
import { useEffect, useLayoutEffect, useState } from "preact/hooks";

import {
    Button,
    Category,
    Checkbox,
    Error,
    IconButton,
    Modal,
    Preloader,
} from "@revoltchat/ui";

import { getDisplayName } from "../../../../lib/displayName";
import { noop } from "../../../../lib/js";

import ChannelIcon from "../../../../components/common/ChannelIcon";
import ServerIcon from "../../../../components/common/ServerIcon";
import Tooltip from "../../../../components/common/Tooltip";
import UserBadges from "../../../../components/common/user/UserBadges";
import UserIcon from "../../../../components/common/user/UserIcon";
import { Username } from "../../../../components/common/user/UserShort";
import UserStatus from "../../../../components/common/user/UserStatus";
import Markdown from "../../../../components/markdown/Markdown";
import { useSession } from "../../../../controllers/client/ClientController";
import { modalController } from "../../../../controllers/modals/ModalController";
import { ModalProps } from "../../types";

function getManager(managers?: string[]) {
    if (!managers || managers.length < 1) {
        return;
    }

    return managers[0];
}

export const UserProfile = observer(
    ({
        user_id,
        isPlaceholder,
        placeholderProfile,
        initialTab,
        channel,
        ...props
    }: ModalProps<"user_profile">) => {
        const [profile, setProfile] = useState<
            undefined | null | API.UserProfile
        >(undefined);
        const [mutual, setMutual] = useState<
            undefined | null | API.MutualResponse
        >(undefined);
        const [isPublicBot, setIsPublicBot] = useState<
            undefined | null | boolean
        >();

        const history = useHistory();
        const session = useSession()!;
        const client = session.client!;
        const [tab, setTab] = useState(initialTab || "profile");

        const user = client.users.get(user_id);
        if (!user) {
            // Зачем они так делают?
            if (props.onClose) useEffect(props.onClose, []);
            return null;
        }

        const ldapInfo = user.ldap_info;

        const member = channel?.server_id
            ? client.members.getKey({
                  server: channel.server_id,
                  user: user_id,
              })
            : undefined;
        const [roles, setRoles] = useState<string[]>(member?.roles ?? []);

        useEffect(() => {
            setRoles(member?.roles ?? []);
        }, [member?.roles]);

        const server_roles = member?.server?.roles ?? {};

        const users = mutual?.users.map((id) => client.users.get(id));

        const mutualGroups = [...client.channels.values()].filter(
            (channel) =>
                channel?.channel_type === "Group" &&
                channel.recipient_ids!.includes(user_id),
        );

        const mutualServers = mutual?.servers.map((id) =>
            client.servers.get(id),
        );

        useLayoutEffect(() => {
            if (!user_id) return;
            if (typeof profile !== "undefined") setProfile(undefined);
            if (typeof mutual !== "undefined") setMutual(undefined);
            if (typeof isPublicBot !== "undefined") setIsPublicBot(undefined);
            // eslint-disable-next-line
        }, [user_id]);

        useEffect(() => {
            if (isPlaceholder) {
                setProfile(placeholderProfile);
            }
        }, [isPlaceholder, placeholderProfile]);

        useEffect(() => {
            if (isPlaceholder) return;
            if (session.state === "Online" && typeof mutual === "undefined") {
                setMutual(null);
                user.fetchMutual().then(setMutual);
            }
        }, [mutual, session.state, isPlaceholder, user]);

        useEffect(() => {
            if (isPlaceholder) return;
            if (session.state === "Online" && typeof profile === "undefined") {
                setProfile(null);

                if (user.permission & UserPermission.ViewProfile) {
                    user.fetchProfile().then(setProfile).catch(noop);
                }
            }
        }, [profile, session.state, isPlaceholder, user]);

        useEffect(() => {
            if (
                session.state === "Online" &&
                user.bot &&
                typeof isPublicBot === "undefined"
            ) {
                setIsPublicBot(null);
                client.bots
                    .fetchPublic(user._id)
                    .then(() => setIsPublicBot(true))
                    .catch(noop);
            }
        }, [isPublicBot, session.state, user, client.bots]);

        const backgroundURL =
            profile &&
            client.generateFileURL(
                profile.background as any,
                { width: 1000 },
                true,
            );

        const badges = user.badges ?? 0;
        const flags = user.flags ?? 0;

        const children = (
            <>
                <div
                    className={styles.header}
                    data-force={profile?.background ? "light" : undefined}
                    style={{
                        backgroundImage:
                            backgroundURL &&
                            `linear-gradient( rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7) ), url('${backgroundURL}')`,
                        paddingBottom: "1px",
                    }}>
                    <div className={styles.profile}>
                        <UserIcon
                            size={80}
                            target={user}
                            status
                            animate
                            hover={typeof user.avatar !== "undefined"}
                            onClick={() =>
                                user.avatar &&
                                modalController.push({
                                    type: "image_viewer",
                                    image: user.avatar,
                                })
                            }
                        />
                        <div className={styles.details}>
                            <div className={styles.usernameDetail}>
                                <span
                                    className={styles.displayname}
                                    onClick={() =>
                                        modalController.writeText(user.username)
                                    }>
                                    {getDisplayName(user)}
                                </span>
                            </div>
                            {user.status?.text && (
                                <span className={styles.status}>
                                    <UserStatus user={user} tooltip />
                                </span>
                            )}
                        </div>
                        {isPublicBot && (
                            <Link to={`/bot/${user._id}`}>
                                <Button
                                    palette="accent"
                                    compact
                                    onClick={props.onClose}>
                                    {"Add to server" /* FIXME: i18n */}
                                </Button>
                            </Link>
                        )}
                        <Localizer>
                            {user.relationship === "User" && !isPlaceholder ? (
                                <Tooltip
                                    content={
                                        <Text id="app.context_menu.to_saved_messages" />
                                    }>
                                    <IconButton
                                        onClick={() => {
                                            const url =
                                                client.savedMessagesChannel
                                                    ?.path;
                                            if (!url) {
                                                return;
                                            }
                                            props.onClose?.();
                                            history.push(url);
                                        }}>
                                        <Notepad size={30} />
                                    </IconButton>
                                </Tooltip>
                            ) : (
                                <Tooltip
                                    content={
                                        <Text id="app.context_menu.message_user" />
                                    }>
                                    <IconButton
                                        onClick={() => {
                                            props.onClose?.();
                                            user.openDM().then((channel) => {
                                                history.push(channel.path);
                                            });
                                        }}>
                                        <Envelope size={30} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Localizer>
                        {user.relationship === "User" && !isPlaceholder && (
                            <IconButton
                                onClick={() => {
                                    props.onClose?.();
                                    history.push(`/settings/profile`);
                                }}>
                                <Edit size={28} />
                            </IconButton>
                        )}
                    </div>
                    {(badges > 0 ||
                        user._id === "01JR7H63NH0M593BYXMWCRQS2P") && (
                        <div
                            style={{
                                marginInline: "1em",
                                padding: "0.5em",
                                background: "var(--primary-background)",
                                borderRadius: "8px",
                                width: "fit-content",
                                backgroundColor:
                                    "rgba(var(--primary-header-rgb), max(var(--min-opacity), 0.65))",
                                backdropFilter: "blur(20px)",
                            }}>
                            <UserBadges badges={badges} uid={user._id} />
                        </div>
                    )}
                    <div className={styles.tabs}>
                        <div
                            data-active={tab === "profile"}
                            onClick={() => setTab("profile")}>
                            <Text id="app.special.popovers.user_profile.profile" />
                        </div>
                        {user.relationship !== "User" && (
                            <>
                                {channel?.server_id &&
                                    channel.server?.havePermission(
                                        "AssignRoles",
                                    ) && (
                                        <div
                                            data-active={tab === "roles"}
                                            onClick={() => setTab("roles")}>
                                            <Text id="app.special.popovers.user_profile.roles" />
                                        </div>
                                    )}
                                <div
                                    data-active={tab === "groups"}
                                    onClick={() => setTab("groups")}>
                                    <Text id="app.special.popovers.user_profile.mutual_groups" />
                                </div>
                                <div
                                    data-active={tab === "servers"}
                                    onClick={() => setTab("servers")}>
                                    <Text id="app.special.popovers.user_profile.mutual_servers" />
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <div className={styles.content}>
                    {tab === "profile" && (
                        <div>
                            {flags & 1 ? (
                                /** ! FIXME: i18n this area */
                                <Category>
                                    <Error error="User is suspended" />
                                </Category>
                            ) : undefined}
                            {flags & 2 ? (
                                <Category>
                                    <Error error="User deleted their account" />
                                </Category>
                            ) : undefined}
                            {flags & 4 ? (
                                <Category>
                                    <Error error="User is banned" />
                                </Category>
                            ) : undefined}
                            {user.bot ? (
                                <>
                                    {/* FIXME: this too */}
                                    <div className={styles.category}>
                                        {"bot owner"}
                                    </div>
                                    <div
                                        onClick={() =>
                                            user.bot &&
                                            modalController.push({
                                                type: "user_profile",
                                                user_id: user.bot.owner,
                                            })
                                        }
                                        className={styles.entry}
                                        key={user.bot.owner}>
                                        <UserIcon
                                            size={32}
                                            target={client.users.get(
                                                user.bot.owner,
                                            )}
                                        />
                                        <span>
                                            <Username
                                                user={client.users.get(
                                                    user.bot.owner,
                                                )}
                                            />
                                        </span>
                                    </div>
                                </>
                            ) : undefined}
                            <div className={styles.category}>
                                <Text id="app.special.popovers.user_profile.sub.information" />
                            </div>
                            <div className={styles.markdown}>
                                <Markdown
                                    content={
                                        [
                                            {
                                                name: "Должность",
                                                value: ldapInfo?.title,
                                            },
                                            {
                                                name: "Отдел",
                                                value: ldapInfo?.department,
                                            },
                                            {
                                                name: "Почта",
                                                value: ldapInfo?.email,
                                            },
                                            {
                                                name: "Место",
                                                value: ldapInfo?.location,
                                            },
                                            {
                                                name: "Руководитель",
                                                value: getManager(
                                                    ldapInfo?.managers,
                                                ),
                                            },
                                        ]
                                            .filter((x) => x.value)
                                            .map((x) => x.name + ": " + x.value)
                                            .join("\n") +
                                        "\n___\n" +
                                        (profile?.content ?? "")
                                    }
                                />
                            </div>
                        </div>
                    )}
                    {tab === "friends" &&
                        (users ? (
                            users.length === 0 ? (
                                <div className={styles.empty}>
                                    <UserPlus size={72} />
                                    <Text id="app.special.popovers.user_profile.no_users" />
                                </div>
                            ) : (
                                <div className={styles.entries}>
                                    {users.map(
                                        (x) =>
                                            x && (
                                                <div
                                                    onClick={() =>
                                                        modalController.push({
                                                            type: "user_profile",
                                                            user_id: x._id,
                                                        })
                                                    }
                                                    className={styles.entry}
                                                    key={x._id}>
                                                    <UserIcon
                                                        size={32}
                                                        target={x}
                                                        status
                                                    />
                                                    <span>
                                                        {getDisplayName(x)}
                                                    </span>
                                                </div>
                                            ),
                                    )}
                                </div>
                            )
                        ) : (
                            <Preloader type="ring" />
                        ))}
                    {tab === "roles" && (
                        <>
                            {Object.keys(server_roles).map((key) => {
                                const role = server_roles[key];
                                return (
                                    <Checkbox
                                        key={key}
                                        value={roles.includes(key) ?? false}
                                        title={
                                            <span
                                                style={{
                                                    color: role.colour!,
                                                }}>
                                                {role.name}
                                            </span>
                                        }
                                        onChange={(v) => {
                                            if (v) {
                                                setRoles([...roles, key]);
                                            } else {
                                                setRoles(
                                                    roles.filter(
                                                        (x) => x !== key,
                                                    ),
                                                );
                                            }
                                        }}
                                    />
                                );
                            })}
                            <Button
                                palette="secondary"
                                disabled={isEqual(member?.roles ?? [], roles)}
                                onClick={() =>
                                    member?.edit({
                                        roles,
                                    })
                                }>
                                <Text id="app.special.modals.actions.save" />
                            </Button>
                        </>
                    )}
                    {tab === "groups" &&
                        (mutualGroups.length === 0 ? (
                            <div className={styles.empty}>
                                <Group size="72" />
                                <Text id="app.special.popovers.user_profile.no_groups" />
                            </div>
                        ) : (
                            <div className={styles.entries}>
                                {mutualGroups.map(
                                    (x) =>
                                        x?.channel_type === "Group" && (
                                            <Link to={`/channel/${x._id}`}>
                                                <div
                                                    className={styles.entry}
                                                    key={x._id}>
                                                    <ChannelIcon
                                                        target={x}
                                                        size={32}
                                                    />
                                                    <span>{x.name}</span>
                                                </div>
                                            </Link>
                                        ),
                                )}
                            </div>
                        ))}
                    {tab === "servers" &&
                        (!mutualServers || mutualServers.length === 0 ? (
                            <div className={styles.empty}>
                                <ListUl size="72" />
                                <Text id="app.special.popovers.user_profile.no_servers" />
                            </div>
                        ) : (
                            <div className={styles.entries}>
                                {mutualServers.map(
                                    (x) =>
                                        x && (
                                            <Link to={`/server/${x._id}`}>
                                                <div
                                                    className={styles.entry}
                                                    key={x._id}>
                                                    <ServerIcon
                                                        target={x}
                                                        size={32}
                                                    />
                                                    <span>{x.name}</span>
                                                </div>
                                            </Link>
                                        ),
                                )}
                            </div>
                        ))}
                </div>
            </>
        );

        if (isPlaceholder) return <div>{children}</div>;

        return (
            <Modal
                {...props}
                nonDismissable={isPlaceholder}
                transparent
                maxWidth="560px">
                {children}
            </Modal>
        );
    },
);
